import { FileFillerInterface } from '../../interfaces/file-filler.interface';
import { CalculationResponse } from '../../responses/calculation.response';
import { ExportFileInterface } from '../../interfaces/export-file.interface';
import { ExportTypeEnum } from '../../enums/export-type.enum';
import { Workbook, Worksheet } from 'exceljs';
import { FieldTypeEnum } from '../../enums/field-type.enum';
import { SalaryDividendResult } from '../../results/calculations/salary-dividend/salary-dividend.result';
import { SalaryDividendOptions } from '../../options/calculations/salary-dividend/salary-dividend.options';

enum ColIndexEnum {
  TITLE = 'A',
  NAME = 'B',
  SALARY = 'C',
  DIVIDEND = 'E',
  OPTITMUM = 'G'
}

export class SalaryDividendXlsxFiller implements FileFillerInterface {

  private resultsSheet;
  private rowIndex: number;

  constructor() {
  }

  async fill(calculation: CalculationResponse): Promise<ExportFileInterface> {
    this.rowIndex = 1;

    const workbook = new Workbook();
    this.resultsSheet = workbook.addWorksheet('Résultats', {});

    this.mapResultsSection1(calculation);
    this.mapResultsSection2(calculation);
    this.mapResultsNote1(calculation);
    this.mapResultsNote2(calculation);
    this.mapResultsNote3(calculation);

    this.resultsSheet.getColumn('B').width = 64;
    this.resultsSheet.getColumn('C').width = 20;
    this.resultsSheet.getColumn('D').width = 10;
    this.resultsSheet.getColumn('E').width = 20;
    this.resultsSheet.getColumn('F').width = 10;
    this.resultsSheet.getColumn('G').width = 20;

    const buffer = await workbook.xlsx.writeBuffer();
    console.log('WRITING FILE');

    return {
      fileName: `${calculation.title}.xlsx`,
      fileBytes: buffer,
      fileType: ExportTypeEnum.XLSX,
    };
  }

  salaryDividendOptimumHeader() {
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.SALARY, this.rowIndex, 'Salaire', undefined, true);
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.DIVIDEND, this.rowIndex, 'Dividende', undefined, true);
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.OPTITMUM, this.rowIndex, 'Optimisation', undefined, true);
    this.rowIndex++;
  }

  fillResultLine(name: string, salaryValue?: number, dividendValue?: number, optimumValue?: number) {
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.NAME, this.rowIndex, name);
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.SALARY, this.rowIndex, salaryValue ?? 'N/A', FieldTypeEnum.Money);
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.DIVIDEND, this.rowIndex, dividendValue ?? 'N/A', FieldTypeEnum.Money);
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.OPTITMUM, this.rowIndex, optimumValue ?? 'N/A', FieldTypeEnum.Money);
    this.rowIndex++;
  }

  mapResultsSection1(calculation: CalculationResponse, locale: string = 'fr'): void {
    const results = calculation.results?.results as SalaryDividendResult;
    const options = calculation.options?.options as SalaryDividendOptions;

    // Titre
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.TITLE, this.rowIndex, 'Liquidité disponible personnelle');
    this.rowIndex++;

    this.salaryDividendOptimumHeader();
    this.fillResultLine('Dividende ordinaire', 0, results?.withDividendOnly?.realDividend?.nonEligible, results?.optimum?.realDividend?.nonEligible);
    this.fillResultLine('Dividende déterminée', 0, results?.withDividendOnly?.realDividend?.eligible, results?.optimum?.realDividend?.eligible);
    this.fillResultLine('Salaire', results?.withSalaryOnly?.salary, 0, results?.optimum?.salary);
    this.fillResultLine('Cotisation de l\'employé supplémentaire (Note 1)', results?.withSalaryOnly?.shareholderContributionExcedent, results?.withDividendOnly?.shareholderContributionExcedent, results?.optimum?.shareholderContributionExcedent);
    this.fillResultLine('Impôt personnel supplémentaire (Note 2)', results?.withSalaryOnly?.shareholderTaxExcedent, results?.withDividendOnly?.shareholderTaxExcedent, results?.optimum?.shareholderTaxExcedent);
    this.fillResultLine('Liquidité disponible personnelle (coût de vie)', options?.costOfLiving, options?.costOfLiving, options?.costOfLiving);

    this.rowIndex++;
  }

  mapResultsSection2(calculation: CalculationResponse, locale: string = 'fr'): void {
    const results = calculation.results?.results as SalaryDividendResult;
    const options = calculation.options?.options as SalaryDividendOptions;

    // Titre
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.TITLE, this.rowIndex, 'Déboursé corporatif');
    this.rowIndex++;

    this.salaryDividendOptimumHeader();
    this.fillResultLine('Dividende ordinaire', 0, results?.withDividendOnly?.realDividend?.nonEligible, results?.optimum?.realDividend?.nonEligible);
    this.fillResultLine('Dividende déterminée', 0, results?.withDividendOnly?.realDividend?.eligible, results?.optimum?.realDividend?.eligible);
    this.fillResultLine('Salaire', results?.withSalaryOnly?.salary, 0, results?.optimum?.salary);
    this.fillResultLine('Cotisation de l\'employeur (Note 3)', results?.withSalaryOnly?.employerContribution, results?.withDividendOnly?.employerContribution, results?.optimum?.employerContribution);
    this.fillResultLine('Déboursé corporatif', results?.withSalaryOnly?.corporate?.cashDisbursement, results?.withDividendOnly?.corporate?.cashDisbursement, results?.optimum?.corporate?.cashDisbursement);

    this.rowIndex++;
  }

  mapResultsNote1(calculation: CalculationResponse, locale: string = 'fr'): void {
    const results = calculation.results?.results as SalaryDividendResult;
    const options = calculation.options?.options as SalaryDividendOptions;

    // Titre
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.TITLE, this.rowIndex, 'Note 1 - Cotisations de l\'employé supplémentaires');
    this.rowIndex++;

    this.salaryDividendOptimumHeader();
    this.fillResultLine('RRQ après la simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11);
    this.fillResultLine('RQAP après la simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.line37,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.line37,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.line37);
    this.fillResultLine('AE après la simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule10?.line20,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule10?.line20,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule10?.line20);
    this.fillResultLine('Total des cotisations de l\'employé après la simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.employeeContributions,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.employeeContributions,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.employeeContributions);
    this.fillResultLine('RRQ avant la simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11);
    this.fillResultLine('RQAP avant la simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.line37,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.line37,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.line37);
    this.fillResultLine('Total des cotisations de l\'employé avant la simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.employeeContributions,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.employeeContributions,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.employeeContributions);
    this.fillResultLine('Cotisations de l\'employé supplémentaires',
      results?.withSalaryOnly?.shareholderContributionExcedent,
      results?.withDividendOnly?.shareholderContributionExcedent,
      results?.optimum?.shareholderContributionExcedent);

    this.rowIndex++;
  }

  mapResultsNote2(calculation: CalculationResponse, locale: string = 'fr'): void {
    const results = calculation.results?.results as SalaryDividendResult;
    const options = calculation.options?.options as SalaryDividendOptions;

    // Titre
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.TITLE, this.rowIndex, 'Note 2 - Cotisations de l\'employé supplémentaires');
    this.rowIndex++;

    this.salaryDividendOptimumHeader();
    this.fillResultLine('Impôts personnels fédérals de l\'actionnaire après simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.federal?.lineResult,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.federal?.lineResult,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.federal?.lineResult);
    this.fillResultLine('Impôts personnels Québec de l\'actionnaire après simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.quebec?.line470,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.quebec?.line470,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.quebec?.line470);
    this.fillResultLine('Impôts personnels fédérals du conjoint après simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.spouse?.federal?.lineResult,
      results?.withDividendOnly?.incomeTaxPersonal?.spouse?.federal?.lineResult,
      results?.optimum?.incomeTaxPersonal?.spouse?.federal?.lineResult);
    this.fillResultLine('Impôts personnels Québec du conjoint après simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.spouse?.quebec?.line470,
      results?.withDividendOnly?.incomeTaxPersonal?.spouse?.quebec?.line470,
      results?.optimum?.incomeTaxPersonal?.spouse?.quebec?.line470);
    this.fillResultLine('Total de l\'impôt personnel après la simulation',
      results?.withSalaryOnly?.incomeTaxPersonal?.totalTaxes,
      results?.withDividendOnly?.incomeTaxPersonal?.totalTaxes,
      results?.optimum?.incomeTaxPersonal?.totalTaxes);
    this.fillResultLine('Impôt peronnels supplémentaires',
      results?.withSalaryOnly?.shareholderTaxExcedent,
      results?.withDividendOnly?.shareholderTaxExcedent,
      results?.optimum?.shareholderTaxExcedent);

    this.rowIndex++;
  }

  mapResultsNote3(calculation: CalculationResponse, locale: string = 'fr'): void {
    const results = calculation.results?.results as SalaryDividendResult;
    const options = calculation.options?.options as SalaryDividendOptions;

    // Titre
    this.addValueToSheet(this.resultsSheet, ColIndexEnum.TITLE, this.rowIndex, 'Note 3 - Cotisations de l\'employeur');
    this.rowIndex++;

    this.salaryDividendOptimumHeader();
    this.fillResultLine('RRQ payé par l\'employeur',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule8?.part2?.line11);
    this.fillResultLine('RQAP payé par l\'employeur',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.qPIPEmployerContributions,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.qPIPEmployerContributions,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.scheduleR?.qPIPEmployerContributions);
    this.fillResultLine('AE payé par l\'employeur',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule10?.eIEmployerContributions,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule10?.eIEmployerContributions,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.schedule10?.eIEmployerContributions);
    this.fillResultLine('FSS payé par l\'employeur',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.fSS?.contributions,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.fSS?.contributions,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.fSS?.contributions);
    this.fillResultLine('CNT payé par l\'employeur',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.cNT?.line35,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.cNT?.line35,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.cNT?.line35);
    this.fillResultLine('CNESST payé par l\'employeur',
      results?.withSalaryOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.cNESST?.contribution,
      results?.withDividendOnly?.incomeTaxPersonal?.mainPerson?.salaryContributions?.cNESST?.contribution,
      results?.optimum?.incomeTaxPersonal?.mainPerson?.salaryContributions?.cNESST?.contribution);
    this.fillResultLine('Cotisations de l\'employeur',
      results?.withSalaryOnly?.employerContribution,
      results?.withDividendOnly?.employerContribution,
      results?.optimum?.employerContribution);

    this.rowIndex++;
  }

  private addValueToSheet(sheet: Worksheet, col: ColIndexEnum, row: number, value: any, fieldType?: FieldTypeEnum, isHeader: boolean = false) {
    // console.log(cell)
    sheet.getCell(`${col}${row}`).value = value;

    switch (col) {
      case ColIndexEnum.TITLE:
        sheet.mergeCells(`A${this.rowIndex}:B${this.rowIndex}`);
        sheet.getCell(`${col}${row}`).alignment = { wrapText: true };
        sheet.getCell(`${col}${row}`).style.font = {
          bold: true,
          size: 16,
        };
        break;
      case ColIndexEnum.NAME:
        sheet.getCell(`${col}${row}`).alignment = { wrapText: true };
        break;
      case ColIndexEnum.DIVIDEND:
      case ColIndexEnum.OPTITMUM:
      case ColIndexEnum.SALARY:
        if (fieldType === FieldTypeEnum.Money && typeof value === 'number') {
          sheet.getCell(`${col}${row}`).value = value / 100;
          sheet.getCell(`${col}${row}`).style.numFmt = '"$"#,##0.00;[Red]-"$"#,##0.00';
        } else {
          sheet.getCell(`${col}${row}`).alignment = {
            horizontal: 'right',
          };
        }
        if (isHeader) {
          sheet.getCell(`${col}${row}`).style.font = {
            bold: true,
          };
          sheet.getCell(`${col}${row}`).alignment = {
            horizontal: 'center',
          };
        }
    }
  }
}
